import Vue from 'vue'
import Config from '@/shared/Config'
import { requestUserConfirmation } from './confirmBridge'
import { DESTRUCTIVE_PREFIXES } from './agentIdConventions'

function needsConfirmation (agentId) {
  return DESTRUCTIVE_PREFIXES.some(p => agentId.startsWith(p))
}

function findClickTarget (el) {
  // fpui-button wraps in .button-container > button — find the actual button
  const btn = el.querySelector('button, a')
  return btn || el
}

function findInputTarget (el) {
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    return el
  }
  return el.querySelector('input, textarea, select')
}

export async function executeDOMAction (toolName, input) {
  switch (toolName) {
    case 'click_button': {
      const buttonId = input.button_id
      const el = document.querySelector(`[data-agent="${buttonId}"]`)
      if (!el) throw new Error(`No agent-accessible element: ${buttonId}`)

      if (needsConfirmation(buttonId)) {
        const label = el.textContent?.trim() || buttonId
        const confirmed = await requestUserConfirmation(label)
        if (!confirmed) {
          return { clicked: false, denied: true, button_id: buttonId }
        }
      }

      const target = findClickTarget(el)

      // If the click target is a link that navigates to a different module
      // (different pathname), intercept and return a cross-module signal
      // instead of clicking. The WS handler will close the socket cleanly
      // before navigating — avoids corrupting the backend provider session.
      if (target.tagName === 'A' && target.href) {
        try {
          const linkUrl = new URL(target.href, window.location.origin)
          const linkPath = linkUrl.pathname.replace(/\/+$/, '')
          const currentPath = window.location.pathname.replace(/\/+$/, '')
          if (linkPath && linkPath !== currentPath) {
            return {
              clicked: buttonId,
              crossModuleNavigation: true,
              navigateTo: target.href
            }
          }
        } catch { /* invalid URL — proceed with click */ }
      }

      target.click()
      return { clicked: buttonId }
    }

    case 'fill_form': {
      const fields = input.fields || {}
      const results = {}
      for (const [fieldId, value] of Object.entries(fields)) {
        const el = document.querySelector(`[data-agent="${fieldId}"]`)
        if (!el) {
          results[fieldId] = false
          continue
        }

        const inputEl = findInputTarget(el)
        if (inputEl) {
          // Use native setter to trigger Vue reactivity
          const proto = inputEl.tagName === 'TEXTAREA'
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype
          const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
          if (nativeSetter) {
            nativeSetter.call(inputEl, String(value))
          } else {
            inputEl.value = String(value)
          }
          inputEl.dispatchEvent(new Event('input', { bubbles: true }))
          inputEl.dispatchEvent(new Event('change', { bubbles: true }))
          results[fieldId] = true
        } else {
          results[fieldId] = false
        }
      }
      return { filled: results }
    }

    case 'navigate': {
      let route = input.route

      // Normalize common LLM mistakes before processing
      // 1. Collapse repeated # (e.g. "#/#/id" → "#/id")
      route = route.replace(/#{2,}/g, '#')
      // 2. Doubled module path (e.g. "/connectors/#/connectors/#/id" → "/connectors/#/id")
      route = route.replace(/^\/([^/#]+)\/#\/\1\/#\//, '/$1/#/')

      // Absolute URL — direct navigation (cross-module)
      if (route.startsWith('http://') || route.startsWith('https://')) {
        return {
          navigated: route,
          method: 'location-href',
          crossModuleNavigation: true,
          navigateTo: route
        }
      }

      if (route.startsWith('/')) {
        // Origin-relative path — check if we're changing module (pathname)
        const targetPath = route.split('#')[0].replace(/\/+$/, '')
        const currentPath = window.location.pathname.replace(/\/+$/, '')
        console.debug('[AI Agent navigate] targetPath =', targetPath, 'currentPath =', currentPath, 'route =', route)
        if (targetPath && targetPath !== currentPath) {
          // Cross-module navigation — return signal so the WS handler
          // can close the socket cleanly before navigating.
          return {
            navigated: route,
            method: 'cross-module',
            crossModuleNavigation: true,
            navigateTo: window.location.origin + route
          }
        }
        // Same module — extract hash part and use router or hash fallback
        const hashPart = route.includes('#') ? route.split('#')[1] : route
        const router = Vue.$router
        if (router) {
          try {
            await router.push(hashPart)
            return { navigated: route, method: 'vue-router' }
          } catch {
            // NavigationDuplicated — fallback
          }
        }
        window.location.hash = '#' + hashPart
        return { navigated: route, method: 'hash-fallback' }
      }

      // Hash-only route (e.g. "#/tables" or "/tables")
      const hashRoute = route.startsWith('#') ? route.slice(1) : route
      const router = Vue.$router
      if (router) {
        try {
          await router.push(hashRoute)
          return { navigated: route, method: 'vue-router' }
        } catch {
          // NavigationDuplicated — fallback
        }
      }
      window.location.hash = '#' + hashRoute
      return { navigated: route, method: 'hash-fallback' }
    }

    case 'wait_for_event': {
      const { button_id, timeout = 300 } = input
      const el = document.querySelector(`[data-agent="${button_id}"]`)
      if (!el) throw new Error(`No agent-accessible element: ${button_id}`)

      const target = findClickTarget(el)

      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          target.removeEventListener('click', handler)
          reject(new Error(`Timeout: user did not click ${button_id} within ${timeout}s`))
        }, timeout * 1000)

        function handler () {
          clearTimeout(timer)
          target.removeEventListener('click', handler)
          resolve({ event: 'click', button_id })
        }

        target.addEventListener('click', handler, { once: true })
      })
    }

    case 'read_ui_state': {
      // fpui uses hash routing — the meaningful route is in the hash
      const hash = window.location.hash || '#/'
      const route = hash.replace(/^#/, '')

      const config = await Config()
      const store = window.__fpui_store__

      const session = store?.getters?.SESSION || store?.getters?.KING_SESSION || null
      const user = session
        ? { firstname: session.firstname, lastname: session.lastname, email: session.email }
        : null

      const agentElements = Array.from(document.querySelectorAll('[data-agent]'))
        .filter(el => el.offsetParent !== null)
        .map(el => el.getAttribute('data-agent'))

      // Current module is the first segment of the pathname (e.g. "/lakehouse/" → "lakehouse")
      const module = window.location.pathname.replace(/^\/+/, '').split('/')[0] || 'core'

      return {
        route,
        module,
        url: window.location.href,
        title: document.title,
        project_id: config?.PROJECT_ID || null,
        project_name: config?.PROJECT_NAME || null,
        organization_id: config?.ORGANIZATION_ID || null,
        organization_name: config?.ORGANIZATION_NAME || null,
        user,
        agent_elements: agentElements
      }
    }

    default:
      throw new Error(`Unknown UI tool: ${toolName}`)
  }
}
